import {
  Button,
  ButtonGroup,
  Form,
  FormButton,
  FormControl,
  FormField,
  HelperText,
  isFile,
  Label,
  type Option,
  showNotification,
  Upload,
} from 'platform/components';
import {Box, Heading, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';
import {useController, type UseFormReturn} from 'react-hook-form';

import {
  ImportFileTypeSchema,
  useCreateImportMutation,
  useImportHondaPartsPriceListMutation,
  useImportHondaRecallMutation,
  useImportNissanPriceListMutation,
  useUploadFileMutation,
} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {buildArray, type ApiError} from 'shared/web';

import {importFormSchema, type ImportFormValue} from '../types/ImportFormValue';
import {CloudImage} from './CloudImage';
import {UploadComponent} from './UploadComponent';

const supplierCatalogImportFileTypes = [
  'hondaPartsPriceList',
  'hondaRecall',
  'nissanPriceList',
] as const;

type SupplierCatalogImportFileType = (typeof supplierCatalogImportFileTypes)[number];

function isSupplierCatalogImportFileType(
  fileType: string
): fileType is SupplierCatalogImportFileType {
  return supplierCatalogImportFileTypes.some((candidate) => candidate === fileType);
}

interface ImportFormProps {
  onClose: () => void;
  onSuccess: () => void;
  onError: (error: unknown) => void;
  onSupplierCatalogImportSuccess: () => void;
}

interface SupplierCatalogUploadComponentProps {
  control: FormControl<ImportFormValue>;
  formApi: UseFormReturn<ImportFormValue>;
  importType: string;
}

function SupplierCatalogUploadComponent(props: SupplierCatalogUploadComponentProps) {
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [uploadFile, {isLoading}] = useUploadFileMutation();
  const field = useController({name: 'fileId', control: props.control});

  return (
    <Box width="100%">
      <Label isRequired>{i18n.t('entity.document.actions.uploadDocument')}</Label>
      <Show when={uploadedFileName}>
        <Text size="small">{uploadedFileName}</Text>
      </Show>
      <Upload
        accept={props.importType === 'hondaRecall' ? '.xml' : '.txt'}
        type="card"
        size="minHeight"
        customRequest={({file, onSuccess, onError}) => {
          if (!isFile(file)) {
            return;
          }

          uploadFile({file, namespace: 'prompt-bridge'})
            .unwrap()
            .then((uploaded) => {
              props.formApi.setValue('fileId', uploaded.fileId, {shouldValidate: true});
              props.formApi.setValue('fileName', file.name, {shouldValidate: true});
              setUploadedFileName(file.name);
              onSuccess?.(file);
            })
            .catch((error: ApiError) => {
              handleApiError(error);
              onError?.(
                new Error(i18n.t('entity.documentExtraction.validations.errorUploadingFile'))
              );
            });
        }}
        isDisabled={isLoading}
      />
      <HelperText errorMessage={field.fieldState.error?.message} />
    </Box>
  );
}

export function ImportForm(props: ImportFormProps) {
  const [createImport, createImportQuery] = useCreateImportMutation();
  const [importNissanPriceList, nissanImport] = useImportNissanPriceListMutation();
  const [importHondaPartsPriceList, hondaPartsImport] = useImportHondaPartsPriceListMutation();
  const [importHondaRecall, hondaRecallImport] = useImportHondaRecallMutation();

  const handleSubmit = async (data: ImportFormValue) => {
    if (isSupplierCatalogImportFileType(data.fileType)) {
      const request = {fileId: data.fileId, fileName: data.fileName};
      const importRequest = match(data.fileType)
        .with('nissanPriceList', () => importNissanPriceList(request))
        .with('hondaPartsPriceList', () => importHondaPartsPriceList(request))
        .with('hondaRecall', () => importHondaRecall(request))
        .exhaustive();

      await importRequest
        .unwrap()
        .then(() => showNotification.info(i18n.t('entity.import.alertWarning.message')))
        .then(props.onSupplierCatalogImportSuccess)
        .catch(props.onError);

      return;
    }

    const nativeImportType = ImportFileTypeSchema.safeParse(data.fileType);
    if (!nativeImportType.success) {
      return;
    }

    await createImport({
      body: {
        fileId: data.fileId,
        fileType: nativeImportType.data,
      },
    })
      .unwrap()
      .then(props.onSuccess)
      .catch(props.onError);
  };

  return (
    <Form<ImportFormValue>
      defaultValues={{
        fileId: '',
        fileName: '',
        fileType: '',
      }}
      onSubmit={handleSubmit}
      experimentalZodSchema={importFormSchema}
    >
      {(control, formApi) => {
        const selectedFileType = formApi.watch('fileType');
        const isSupplierCatalogImport = isSupplierCatalogImportFileType(selectedFileType);
        const isLoading =
          createImportQuery.isLoading ||
          nissanImport.isLoading ||
          hondaPartsImport.isLoading ||
          hondaRecallImport.isLoading;

        return (
          <VStack spacing={4}>
            <Box padding={6}>
              <VStack align="center" justify="center" spacing={4}>
                <CloudImage />
                <Heading size={2}>{i18n.t('entity.import.labels.createImportHeading')}</Heading>
                <Box maxWidth={165}>
                  <Text size="small" align="center" color="secondary">
                    {i18n.t('entity.import.labels.createImportDescription')}
                  </Text>
                </Box>
                <Box width="100%">
                  <FormField
                    control={control}
                    name="fileType"
                    type="choice"
                    label={i18n.t('entity.import.labels.fileType')}
                    options={buildArray<Option>([
                      {
                        label: i18n.t('entity.import.labels.carisCost'),
                        value: 'carisSaleVehicleCosts',
                      },
                      {
                        label: i18n.t('entity.import.labels.as3Cost'),
                        value: 'as3SaleVehicleCosts',
                      },
                    ]).whenFeatureEnabled(
                      featureFlags.SUPPLIER_CATALOG_IMPORT,
                      {
                        label: `Honda · ${i18n.t('entity.warehouse.labels.priceList')}`,
                        value: 'hondaPartsPriceList',
                      },
                      {
                        label: `Honda · ${i18n.t('entity.campaigns.labels.serviceCampaigns')}`,
                        value: 'hondaRecall',
                      },
                      {
                        label: `Nissan · ${i18n.t('entity.warehouse.labels.priceList')}`,
                        value: 'nissanPriceList',
                      }
                    )}
                    isRequired
                    isNotClearable
                    onChange={() => {
                      formApi.setValue('fileId', '', {shouldValidate: false});
                      formApi.setValue('fileName', '', {shouldValidate: false});
                    }}
                  />
                </Box>
                <Show when={isSupplierCatalogImport}>
                  <SupplierCatalogUploadComponent
                    key={selectedFileType}
                    control={control}
                    formApi={formApi}
                    importType={selectedFileType}
                  />
                </Show>
                <Show when={!isSupplierCatalogImport}>
                  <UploadComponent
                    key={selectedFileType}
                    name="fileId"
                    control={control}
                    formApi={formApi}
                  />
                </Show>
              </VStack>
            </Box>
            <ButtonGroup align="right">
              <Button
                type="button"
                variant="secondary"
                title={i18n.t('general.labels.discard')}
                onClick={props.onClose}
                isDisabled={isLoading}
              />
              <FormButton
                control={control}
                type="submit"
                title={i18n.t('entity.import.actions.createImportTask')}
                isDisabled={isLoading}
              />
            </ButtonGroup>
          </VStack>
        );
      }}
    </Form>
  );
}
